import type {
	PortalSalesBlock,
	SalesBreakdown,
	StoreScorecard as StoreScorecardData,
	StoreTrend,
} from "@khazzin/api-client";
import { SalesPanels, StoreScorecard } from "@khazzin/ui/analytics";
import { type DateRange, DateRangePicker, presetRange, todayIn } from "@khazzin/ui/patterns";
import { ExportButton } from "@khazzin/ui/patterns/ImportDialog";
import { PageHeader } from "@khazzin/ui/patterns/page";
import { Tabs } from "@khazzin/ui/primitives";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { NotFound } from "../../app/shell/Shell";
import { useStoreScope } from "../../app/shell/scope";
import { levelOf } from "../../app/shell/sections";

/**
 * The Store Portal's Analytics (EP10-S13, DA-10-02), two tabs over one date
 * range, each forced to this store server-side:
 *
 * - **Scorecard** (EP13-S11): the console's `StoreScorecard` in its store
 *   variant over `/portal/scorecard` and its trend, with Export. No rank, no
 *   health chip, no building capacity.
 * - **Sales & Revenue** (EP13-S10): the console's panels over
 *   `/portal/dashboards/sales_revenue`. No store filter and no store ranking.
 *
 * Finance & Cashflow arrives with its provider (EP13-S12).
 */

export function analyticsKey(storeId: string) {
	return ["portal", storeId, "analytics"] as const;
}

/** One Sales & Revenue block for the header's store. */
export function usePortalSales(
	storeId: string,
	block: PortalSalesBlock,
	range: DateRange,
	options: { provinceId?: string | undefined; limit?: number; enabled?: boolean } = {},
) {
	const api = useApi();
	const { provinceId, limit, enabled = true } = options;
	return useQuery<SalesBreakdown>({
		queryKey: [...analyticsKey(storeId), block, range.from, range.to, provinceId ?? "", limit ?? 0],
		queryFn: ({ signal }) =>
			api.getPortalSales(block, {
				...range,
				...(provinceId === undefined ? {} : { provinceId }),
				...(limit === undefined ? {} : { limit }),
				signal,
			}),
		enabled: storeId !== "" && enabled,
		...(block === "top-skus" ? { select: withSkuCodes } : {}),
	});
}

/**
 * The top SKUs by their codes (ADJ-M03-4): the server names them in `names`,
 * and every screen that lists them — the dashboard's card, Analytics' table —
 * then shows `TEE-RED-M` where it showed `sku_…`. A SKU with no name keeps
 * its id.
 */
export function withSkuCodes(data: SalesBreakdown): SalesBreakdown {
	const names = data.names ?? {};
	return {
		...data,
		rows: data.rows.map((row) =>
			row.skuId === null ? row : { ...row, skuId: names[row.skuId] ?? row.skuId },
		),
	};
}

/** The Analytics tabs; the scorecard first (EP13-S11). */
export const ANALYTICS_TABS = ["scorecard", "sales"] as const;
type AnalyticsTab = (typeof ANALYTICS_TABS)[number];

export function AnalyticsPage() {
	const { t, locale } = useLocale();
	const api = useApi();
	const [exportFailed, setExportFailed] = useState(false);
	const { store, context } = useStoreScope();
	const today = todayIn(context?.preferences?.timezone ?? "Asia/Beirut");
	const [range, setRange] = useState<DateRange>(() => presetRange("last30", today));
	const [province, setProvince] = useState<string | undefined>();
	const [params, setParams] = useSearchParams();
	const tab: AnalyticsTab = params.get("tab") === "sales" ? "sales" : "scorecard";
	const storeId = store?.storeId ?? "";
	const open = levelOf(store, "analytics") !== "none";
	const sales = open && tab === "sales";
	const scoring = open && tab === "scorecard" && storeId !== "";

	const summary = usePortalSales(storeId, "summary", range, { enabled: sales });
	const trend = usePortalSales(storeId, "trend", range, { enabled: sales });
	const channels = usePortalSales(storeId, "channels", range, { enabled: sales });
	const geography = usePortalSales(storeId, "geography", range, {
		provinceId: province,
		enabled: sales,
	});
	const topSkus = usePortalSales(storeId, "top-skus", range, { enabled: sales });
	const scorecard = useQuery<StoreScorecardData>({
		queryKey: [...analyticsKey(storeId), "scorecard", range.from, range.to],
		queryFn: ({ signal }) => api.getPortalScorecard({ ...range, signal }),
		enabled: scoring,
	});
	const scoreTrend = useQuery<StoreTrend>({
		queryKey: [...analyticsKey(storeId), "scorecard-trend", range.from, range.to],
		queryFn: ({ signal }) => api.getPortalScorecardTrend({ ...range, signal }),
		enabled: scoring,
	});

	if (store !== null && !open) {
		return <NotFound />;
	}
	const names = new Map(Object.entries(geography.data?.names ?? {}));

	return (
		<div className="flex flex-col gap-4">
			<PageHeader
				title={t("portal.analytics.title")}
				subtitle={t("portal.analytics.subtitle")}
				{...(tab === "scorecard"
					? {
							primaryAction: (
								<ExportButton
									type="store_scorecard"
									t={t}
									api={{
										downloadExport: (_, __, format) => api.downloadPortalScorecard(range, format),
									}}
									onError={() => setExportFailed(true)}
								/>
							),
						}
					: {})}
			/>
			{exportFailed && (
				<p role="alert" className="text-[12.5px] text-danger-fg">
					{t("portal.analytics.exportFailed")}
				</p>
			)}
			<DateRangePicker value={range} onChange={setRange} today={today} t={t} />
			<Tabs
				aria-label={t("portal.analytics.tabs.label")}
				value={tab}
				onValueChange={(value) =>
					setParams(value === "scorecard" ? {} : { tab: value }, { replace: true })
				}
				tabs={ANALYTICS_TABS.map((value) => ({
					value,
					label: t(`portal.analytics.tabs.${value}`),
				}))}
			/>
			{tab === "scorecard" ? (
				<StoreScorecard
					t={t}
					locale={locale}
					variant="store"
					scorecard={scorecard}
					trend={scoreTrend}
				/>
			) : (
				<SalesPanels
					t={t}
					locale={locale}
					variant="store"
					range={range}
					compare
					province={province}
					blocks={{ summary, trend, channels, geography, topSkus }}
					names={{ province: names, city: names }}
					onDrill={(id) => setProvince(id ?? undefined)}
				/>
			)}
		</div>
	);
}
